import { useMemo } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import createDOMPurify from "dompurify";
import { Link } from "react-router-dom";

import { useGetDataBlockReferencesQuery } from "@/redux/service";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import ChaptersIcon from "@/assets/icons/chapters.svg?react";
import ChatIcon from "@/assets/icons/chat.svg?react";
import ReportsIcon from "@/assets/icons/reports-icon.svg?react";
import AnalysisIcon from "@/assets/icons/analysis-icon.svg?react";
import CommunityIcon from "@/assets/icons/community.svg?react";
import {
  DataBlockReference,
  DataBlockReferencesProps,
  DataBlockReferencesTypes,
} from "@/features/datablocks/details/types";

import "../styles.css";

const DataBlockReferences = ({ dataBlock }: DataBlockReferencesProps) => {
  const { t } = useTranslation();
  const DOMPurify = createDOMPurify(window);

  const {
    data: references,
    isFetching,
    isSuccess,
  } = useGetDataBlockReferencesQuery(dataBlock.id);

  const referencesTypes: DataBlockReferencesTypes = {
    surveys: {
      title: t("datablocks.labels.surveys"),
      icon: ReportsIcon,
      getLink: (value: DataBlockReference) => `/reports/${value.id}`,
    },
    surveyViews: {
      title: t("datablocks.labels.surveyViews"),
      icon: AnalysisIcon,
      getLink: (value: DataBlockReference) => `/reports/views/${value.id}`,
    },
    chapters: {
      title: t("datablocks.labels.chapters"),
      icon: ChaptersIcon,
      getLink: (value: DataBlockReference) => `/evidence/${value.id}`,
    },
    discussions: {
      title: t("datablocks.labels.discussions"),
      icon: CommunityIcon,
      getLink: (value: DataBlockReference) => `/discussons/${value.id}`,
    },
    comments: {
      title: t("datablocks.labels.comments"),
      icon: ChatIcon,
      getLink: (value: DataBlockReference) =>
        `/discussons/${value.discussionId}`,
    },
  };

  const noReferencesExist = useMemo(() => {
    if (!references) return true;

    return Object.values(references)
      .map((entry) => entry.length === 0)
      .every(Boolean);
  }, [references]);

  const referencesData = useMemo(() => {
    if (isSuccess) {
      if (noReferencesExist) {
        return (
          <div className="body-s-regular">
            {t("datablocks.labels.noReferencesExist")}
          </div>
        );
      } else {
        return Object.entries(references).map(
          ([referenceType, referenceDetails]) => {
            const referenceTypeDetails =
              referencesTypes[referenceType as keyof DataBlockReferencesTypes];
            if (!referenceDetails) return;

            return (
              referenceDetails.length > 0 && (
                <div key={referenceType}>
                  <div className="data-block-reference-label-container flex gap-100 items-center">
                    <referenceTypeDetails.icon />
                    <h6 className="body-s-medium">
                      {referenceTypeDetails.title}
                    </h6>
                  </div>

                  <ul>
                    {referenceDetails.map((value: DataBlockReference) => {
                      const getLink = referenceTypeDetails.getLink;

                      return (
                        <li key={value.label}>
                          <Link
                            className="data-block-reference-list-item"
                            to={getLink(value)}
                          >
                            {referenceType === "comments" ? (
                              <div
                                style={{ wordWrap: "break-word" }}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    _.truncate(value.label, { length: 80 }),
                                    {
                                      USE_PROFILES: { html: true },
                                    },
                                  ),
                                }}
                              />
                            ) : (
                              value.label
                            )}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )
            );
          },
        );
      }
    }
  }, [references, noReferencesExist]);

  return (
    <div className="flex flex-col gap-200">
      <div className="flex gap-100 items-center justify-between">
        <h2 className="header-m-medium">{t("datablocks.labels.references")}</h2>
        <BaseTooltip title={t("datablocks.labels.referencesInfo")}>
          <InfoCircleOutlined />
        </BaseTooltip>
      </div>
      <div className="flex flex-col gap-300">
        {isFetching && <BaseSpinner size="small" />}
        {isSuccess && referencesData}
      </div>
    </div>
  );
};

export default DataBlockReferences;
